/**
 * 90-day UGC rollout prioritiser.
 *
 * Takes the gaps a merchant already knows about (from the UGC audit,
 * AI Visibility Check, PDP audit or a manual self-assessment) plus
 * three business-size inputs, and sequences them into three 30-day
 * phases. This is a prioritisation heuristic, not a revenue forecast:
 * it never outputs a dollar figure or a claimed conversion lift for a
 * specific gap, only a priority ordering and the Idukki module that
 * closes each one. See lib/copy/idukki-articles.ts CLAUDE.md content-
 * integrity rule: no invented customer numbers. Priority scores here
 * are Idukki's own product-sequencing judgement, not a measured stat.
 */

export type GapId =
  | 'collection'
  | 'schema'
  | 'video'
  | 'social-embed'
  | 'ai-citability'
  | 'rights'
  | 'measurement'
  | 'mobile-perf';

export type GapDef = {
  id: GapId;
  label: string;
  detail: string;
  module: string;
  action: string;
  /** 1 (nice-to-have) to 5 (foundational) — Idukki's own sequencing judgement. */
  baseSeverity: number;
};

export const GAP_CATALOG: GapDef[] = [
  {
    id: 'collection',
    label: 'No UGC or review widget live',
    detail: 'Nothing on the homepage or PDPs surfaces customer photos, video or reviews yet.',
    module: 'Channels + widgets — UGC collection',
    action: 'Turn on Instagram + TikTok collection and connect Google, Trustpilot or Feefo reviews.',
    baseSeverity: 5,
  },
  {
    id: 'video',
    label: 'No shoppable video',
    detail: 'The highest-intent format (customer video near the buy button) is not in use.',
    module: 'Channels + widgets — Shoppable Video',
    action: 'Launch a shoppable video gallery on your top-selling PDPs first.',
    baseSeverity: 5,
  },
  {
    id: 'social-embed',
    label: 'No live social feed on-site',
    detail: 'Content customers are already posting never reaches the storefront.',
    module: 'Channels + widgets — social feed',
    action: 'Embed a live Instagram/TikTok feed on the homepage and top category pages.',
    baseSeverity: 3,
  },
  {
    id: 'schema',
    label: 'No structured review/product schema',
    detail: 'Reviews and product data exist but are not marked up, so search and AI engines can\'t read them.',
    module: 'Data + revenue — schema markup',
    action: 'Publish complete Product + AggregateRating + Review JSON-LD on every PDP.',
    baseSeverity: 4,
  },
  {
    id: 'ai-citability',
    label: 'Not visible to AI shopping engines',
    detail: 'ChatGPT, Claude, Perplexity and Gemini can\'t reliably find or cite your pages.',
    module: 'Data + revenue — AI/GEO readiness',
    action: 'Allow AI-bot crawl in robots.txt, publish llms.txt, and complete Open Graph tags.',
    baseSeverity: 3,
  },
  {
    id: 'rights',
    label: 'No rights-management workflow',
    detail: 'Customer content is being reposted without a formal usage-rights record.',
    module: 'Trust + safety — Rights Management',
    action: 'Turn on Rights Management so every repost has a documented, timestamped grant.',
    baseSeverity: 4,
  },
  {
    id: 'measurement',
    label: 'No UGC attribution or analytics',
    detail: 'There is no way to see which content or creator is actually driving revenue.',
    module: 'Data + revenue — analytics',
    action: 'Wire widget interactions into GA4 so lift is measured per placement, not assumed.',
    baseSeverity: 2,
  },
  {
    id: 'mobile-perf',
    label: 'Mobile / technical hygiene gaps',
    detail: 'Missing canonical tags or viewport meta undermine everything layered on top.',
    module: 'Testing + optimisation — performance',
    action: 'Fix canonical URLs and mobile viewport hygiene before adding more on-page weight.',
    baseSeverity: 1,
  },
];

const GAP_BY_ID = new Map(GAP_CATALOG.map((g) => [g.id, g]));

/** Maps a raw check id from any existing audit tool onto one or more canonical gaps. */
export const RAW_CHECK_MAP: Record<string, GapId[]> = {
  // lib/ugcHomepageAudit.ts (UGC audit)
  'ugc-widget': ['collection'],
  'review-schema': ['schema'],
  video: ['video'],
  'social-embed': ['social-embed'],
  canonical: ['mobile-perf'],
  'mobile-viewport': ['mobile-perf'],
  // Manual rights/measurement toggles (components/marketing/ugc-audit-checklist.tsx)
  r1: ['rights'],
  r2: ['rights'],
  m1: ['measurement'],
  m2: ['measurement'],
  // lib/aiVisibilityAudit.ts (AI Visibility Check / AEO brand report)
  'product-schema': ['schema', 'ai-citability'],
  'aggregate-rating': ['schema'],
  'named-reviews': ['schema'],
  'robots-ai': ['ai-citability'],
  'llms-txt': ['ai-citability'],
  'agent-tooling': ['ai-citability'],
  'opengraph-twitter': ['ai-citability'],
  'meta-description': ['ai-citability'],
};

export function gapIdsFromRawChecks(rawIds: string[]): GapId[] {
  const set = new Set<GapId>();
  for (const raw of rawIds) {
    for (const g of RAW_CHECK_MAP[raw] ?? []) set.add(g);
  }
  return Array.from(set);
}

export type Category = { id: string; name: string };

// Kept aligned with components/tools/ugc-revenue-calculator.tsx's industry
// list so a merchant sees the same category names across tools.
export const CATEGORIES: Category[] = [
  { id: 'fashion', name: 'Fashion & apparel' },
  { id: 'beauty', name: 'Beauty & cosmetics' },
  { id: 'jewelry', name: 'Accessories & jewelry' },
  { id: 'furniture', name: 'Furniture & home' },
  { id: 'hospitality', name: 'Hospitality & travel' },
  { id: 'electronics', name: 'Consumer electronics' },
  { id: 'sporting', name: 'Sporting goods' },
  { id: 'food', name: 'Food & beverage' },
  { id: 'other', name: 'Other' },
];

// Categories where video is disproportionately persuasive (fit, texture,
// motion). Nudges 'video' priority up a notch, mirroring the emphasis
// already used for these categories in ugc-revenue-calculator.tsx.
const VIDEO_LED_CATEGORIES = new Set(['fashion', 'beauty', 'sporting']);

export type PlannedGap = GapDef & { priority: number; phase: 1 | 2 | 3 };

export type PlanInput = {
  gapIds: GapId[];
  categoryId: string;
  aov: number;
  monthlySessions: number;
};

export type Plan = {
  phases: { phase: 1 | 2 | 3; title: string; window: string; items: PlannedGap[] }[];
  flat: PlannedGap[];
};

function trafficMultiplier(sessions: number): number {
  if (sessions >= 250_000) return 1.6;
  if (sessions >= 50_000) return 1.3;
  if (sessions >= 5_000) return 1.0;
  return 0.7;
}

function aovMultiplier(aov: number): number {
  if (aov >= 300) return 1.4;
  if (aov >= 100) return 1.2;
  if (aov >= 30) return 1.0;
  return 0.85;
}

export function computePlan({ gapIds, categoryId, aov, monthlySessions }: PlanInput): Plan {
  const uniqueIds = Array.from(new Set(gapIds));
  const chosen = uniqueIds.length > 0 ? uniqueIds : GAP_CATALOG.map((g) => g.id);
  const tMul = trafficMultiplier(monthlySessions);
  const aMul = aovMultiplier(aov);
  const videoLed = VIDEO_LED_CATEGORIES.has(categoryId);

  const scored: PlannedGap[] = chosen
    .map((id) => GAP_BY_ID.get(id))
    .filter((g): g is GapDef => !!g)
    .map((g) => {
      const categoryBump = videoLed && g.id === 'video' ? 1.2 : 1;
      const priority = Math.round(g.baseSeverity * tMul * aMul * categoryBump * 10) / 10;
      return { ...g, priority, phase: 1 as const };
    })
    .sort((a, b) => b.priority - a.priority);

  // Foundational technical fixes (mobile-perf) never lead a phase, even
  // if traffic/AOV inflate their score, they are prerequisites, not wins.
  const ordered = [
    ...scored.filter((g) => g.id !== 'mobile-perf'),
    ...scored.filter((g) => g.id === 'mobile-perf'),
  ];

  const perPhase = Math.max(1, Math.ceil(ordered.length / 3));
  const phase1 = ordered.slice(0, perPhase).map((g) => ({ ...g, phase: 1 as const }));
  const phase2 = ordered.slice(perPhase, perPhase * 2).map((g) => ({ ...g, phase: 2 as const }));
  const phase3 = ordered.slice(perPhase * 2).map((g) => ({ ...g, phase: 3 as const }));

  return {
    phases: [
      { phase: 1 as const, title: 'Days 0-30: foundations', window: '0-30', items: phase1 },
      { phase: 2 as const, title: 'Days 30-60: expand', window: '30-60', items: phase2 },
      { phase: 3 as const, title: 'Days 60-90: compound', window: '60-90', items: phase3 },
    ].filter((p) => p.items.length > 0),
    flat: [...phase1, ...phase2, ...phase3],
  };
}
